import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, map, of, switchMap, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { createActionResponseFactory } from '@core/ngrx-legacy';
import { ReportsComponentsActions, ReportsDashboardsActions } from '@core/reports-data';
import type {
  ReportDashboardComponent,
  ReportDashboardComponentCreate,
  ReportDashboardComponentViewData,
} from '@core/reports-types';
import { DEFAULT_COMPONENT_GRID_POSITION } from '@core/reports-types';
import { getNextGridPosition, GridPositionHelper } from '@core/reports-util';
import { NotificationsController } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { ReportComponentsDataFacade } from './components-data.facade';
import { ReportDashboardsFacade } from './dashboards.facade';
import { beforeComponentCreate, toComponentViewData } from '../helpers/facade-helpers';

@Injectable({
  providedIn: 'root',
})
export class ReportComponentsCrudActionsFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly componentsData = inject(ReportComponentsDataFacade);
  private readonly dashboards = inject(ReportDashboardsFacade);
  private readonly i18n = inject(I18nService);
  private readonly notificationsCtrl = inject(NotificationsController);

  public onComponentCreate$: Observable<ReportDashboardComponentCreate> = this.actions$.pipe(
    ofType(ReportsComponentsActions.createComponent, ReportsComponentsActions.duplicateComponent),
    map(action => action.payload.data.component),
  );

  private actionResponseFactory = createActionResponseFactory(ReportsComponentsActions);

  /**
   *
   * @param dashboardId - if not specified, active dashboard id will be used
   */
  public createDashboardComponent(
    component: ReportDashboardComponentCreate,
    dashboardId?: number,
  ): void {
    const correlationId = generateUuid();

    const dashboardId$ = this.dashboardId$(dashboardId);
    const dashboardGrid$ = this.dashboards.getDashboardGridComponents(dashboardId);

    combineLatest([dashboardId$, dashboardGrid$])
      .pipe(
        map(([dashboardId, dashboardGrid]) => ({
          correlationId,
          data: {
            dashboardId,
            component: beforeComponentCreate(component, correlationId, {
              resolveGridPosition: true,
              dashboardGrid,
            }),
          },
        })),
        switchMap(payload =>
          this.actionResponseFactory(
            ReportsComponentsActions.createComponent,
            {
              payload,
            },
            {
              success: ReportsComponentsActions.createOneOptimisticSuccess,
              failed: ReportsComponentsActions.createOneOptimisticFailed,
            },
          ),
        ),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.component_added_created'),
          );
        }
      });
  }

  /**
   *
   * @param dashboardId - if not specified, active dashboard id will be used
   */
  public createManyDashboardComponents(
    components: ReportDashboardComponentViewData[],
    dashboardId?: number,
  ): void {
    combineLatest([
      this.dashboards.getDashboardGridComponents(dashboardId),
      this.dashboardId$(dashboardId),
    ]).subscribe(([grid, dashboardId]) => {
      const gridHelper = new GridPositionHelper(grid);

      const componentsData = components.map(c => {
        const correlationId = generateUuid();
        const componentPosition = gridHelper.getFirstPossiblePosition(
          DEFAULT_COMPONENT_GRID_POSITION,
        );
        const component = {
          ...toComponentViewData(c),
          grid: gridHelper.addItem(componentPosition),
        };
        return {
          component: beforeComponentCreate(component, correlationId, {
            resolveGridPosition: false,
          }),
          correlationId,
        };
      });

      this.store.dispatch(
        ReportsComponentsActions.createMany({
          payload: {
            dashboardId,
            componentsData,
          },
        }),
      );
    });
  }

  /**
   *
   * @param dashboardId - if not specified, active dashboard id will be used
   */
  public duplicateDashboardComponent(
    component: ReportDashboardComponent,
    dashboardId?: number,
  ): void {
    const correlationId = generateUuid();

    combineLatest([
      this.dashboards.getDashboardGridComponents(dashboardId),
      this.dashboardId$(dashboardId),
    ])
      .pipe(
        switchMap(([grid, dashboardId]) =>
          this.actionResponseFactory(ReportsComponentsActions.duplicateComponent, {
            payload: {
              correlationId,
              data: {
                dashboardId,
                component: {
                  ...beforeComponentCreate(component, correlationId, {
                    resolveGridPosition: true,
                    dashboardGrid: grid,
                  }),
                  name: `${component.name} (${this.i18n.translate('app.common.copy')})`,
                  origin: 'duplicate',
                },
              },
            },
          }),
        ),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.component_duplicated'),
          );
        }
      });
  }

  /**
   *
   * @param dashboardId - if not specified, active dashboard id will be used
   */
  public deleteDashboardComponent(component: ReportDashboardComponent, dashboardId?: number): void {
    this.dashboardId$(dashboardId)
      .pipe(
        switchMap(dashboardId =>
          this.actionResponseFactory(
            ReportsComponentsActions.removeComponent,
            {
              payload: {
                data: {
                  dashboardId,
                  component,
                },
              },
            },
            {
              failed: ReportsComponentsActions.removeOneOptimisticFailed,
              success: ReportsComponentsActions.removeOneOptimisticSuccess,
            },
          ),
        ),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.component_deleted'),
          );
        }
      });
  }

  /**
   *
   * @param dashboardId - if not specified, active dashboard id will be used
   */
  public updateDashboardComponent(component: ReportDashboardComponent, dashboardId?: number): void {
    this.dashboardId$(dashboardId)
      .pipe(
        switchMap(dashboardId =>
          this.actionResponseFactory(
            ReportsComponentsActions.updateComponent,
            {
              payload: {
                correlationId: component.localId ?? generateUuid(),
                data: {
                  component: component,
                  dashboardId,
                },
              },
            },
            {
              success: ReportsComponentsActions.updateOneOptimisticSuccess,
              failed: ReportsComponentsActions.updateOneOptimisticFailed,
            },
          ),
        ),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.component_edited_saved'),
          );
        }
      });
  }

  /**
   *
   * @param componentId
   * @param targetDashboardId
   * @param sourceDashboardId - if not specified, active dashboard id will be used
   */
  public moveDashboardComponent(
    componentId: number,
    targetDashboardId: number,
    sourceDashboardId?: number,
  ): void {
    const component$ = this.componentsData.getComponent(componentId).pipe(filterBoolean, take(1));
    const targetDashboardGrid$ = this.dashboards.getDashboardGridComponents(targetDashboardId);
    const sourceDashboardId$ = this.dashboardId$(sourceDashboardId);

    combineLatest([component$, targetDashboardGrid$, sourceDashboardId$])
      .pipe(
        switchMap(([component, dashboardGrid, sourceDashboardId]) => {
          const grid = getNextGridPosition(dashboardGrid, component.grid);
          return this.actionResponseFactory(
            ReportsDashboardsActions.moveComponent,
            {
              payload: {
                componentId,
                sourceDashboardId,
                targetDashboardId,
                localId: component.localId ?? null,
                grid,
              },
            },
            {
              success: ReportsDashboardsActions.moveComponentSuccess,
              failed: ReportsDashboardsActions.moveComponentFailed,
            },
          );
        }),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.component_moved'),
          );
        }
      });
  }

  public localUpdateDashboardComponent(component: ReportDashboardComponent): void {
    this.store.dispatch(
      ReportsComponentsActions.updateOneOptimisticSuccess({
        payload: {
          correlationId: `${component.id ?? component.localId ?? generateUuid()}`,
          data: component,
        },
      }),
    );
  }

  private dashboardId$(dashboardId?: number): Observable<number> {
    return dashboardId
      ? of(dashboardId)
      : this.dashboards.activeDashboard$.pipe(
          take(1),
          map(dashboard => dashboard.id),
        );
  }
}
